import React, { useEffect } from 'react';
import ReactGA from 'react-ga';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import HomePage from './pages/homepage/HomePage';
import ProfilePage from './pages/profile/ProfilePage';
import SignInPage from './pages/auth/SignInPage';
import WordPressPage from './pages/WordPressPage';
import * as history from 'history';
import { dynamicPathSlugs, reactPageSlugs } from './constants/reactPageSlugs';
import { Routes, YSaatioRoutes } from './constants/routes';
import { useApp } from './context/appContext';
import { flattenMenuItems } from './utils/wordpress';
import BillingPage from './pages/billing/BillingPage';
import AuthenticatedRoute from './AuthenticatedRoute';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import SignUpPage from './pages/auth/SignUpPage';
import { loadMenu } from './context/appActions';
import { useKennoTenantState } from './context/kennoTenantContext';
import ContactRequestSuccessPage from './pages/contact/ContactRequestSuccessPage';
import { MeetingMinutesPage } from './pages/forms/meeting-minutes/MeetingMinutesPage';
import { ActionPlanPage } from './pages/forms/action-plan/ActionPlanPage';
import { ActionReportPage } from './pages/forms/action-report/ActionReportPage';
import { EventInvitePage } from './pages/forms/event-invite/EventInvitePage';
import YSaatioSignInPage from './pages/y-saatio/YSaatioSignInPage';
import YSaatioResetPasswordPage from './pages/y-saatio/YSaatioResetPasswordPage';
import YSaatioSignUpPage from './pages/y-saatio/YSaatioSignUpPage';
import OwnFormsPage from './pages/profile/OwnFormsPage';
import FormPage from './pages/profile/FormPage';
import UpdateFormPage from './pages/profile/UpdateFormPage';
import { YSaatioTerminationHomePage } from './pages/y-saatio/termination/YSaatioTerminationHomePage';
import YSaatioTerminationFormPage from './pages/y-saatio/termination/YSaatioTerminationFormPage';
import FormReviewPage from './pages/admin/FormReviewPage';
import { GeneralExpenseReportPage } from './pages/forms/general-expense-report/GeneralExpensereportPage';
import { TravelExpenseReportPage } from './pages/forms/travel-expense-report/TravelExpenseReportPage';
import ImpersonationLoginPage from './pages/admin/impersonate/ImpersonationLoginPage';

const customHistory = history.createBrowserHistory();

const getPageComponent = (pageSlug: string) => (pageSlug in reactPageSlugs ? reactPageSlugs[pageSlug] : WordPressPage);

/**
 * Get component for rendering a route with a dynamic section, i.e. /tiedotteet/{id-goes-here}
 */
const getDynamicPathPageComponent = (pageSlug: string) =>
  pageSlug in dynamicPathSlugs ? dynamicPathSlugs[pageSlug] : null;

const RoutingRules = () => {
  const [{ menu, language }, appDispatch] = useApp();
  const { user } = useKennoTenantState();
  const menuItems = menu.items.length > 0 && flattenMenuItems(menu.items);

  useEffect(() => {
    return customHistory.listen((location) => ReactGA.pageview(location.pathname));
  }, []);

  useEffect(() => {
    loadMenu(appDispatch, language);
  }, [appDispatch, language, user.isLoggedIn]); // Ensure menu gets re-loaded in case user signs in and out

  const menuRoutes: React.ReactNode[] = [];
  if (menuItems) {
    menuItems.forEach((item) => {
      if (item.parentId === 0) return;
      menuRoutes.push(
        <AuthenticatedRoute
          exact
          key={item.title}
          path={item.path}
          component={getPageComponent(item.slug)}
          slug={item.slug}
        />
      );
      if (item.slug in dynamicPathSlugs) {
        const component = getDynamicPathPageComponent(item.slug);
        if (component) {
          menuRoutes.push(
            <AuthenticatedRoute
              exact
              key={item.title}
              path={`${item.path}/:id`}
              component={component}
              slug={item.slug}
            />
          );
        }
      }
    });
  }

  const ySaatioContractTerminationRoutes = [
    <Route key={YSaatioRoutes.SIGN_UP} path={YSaatioRoutes.SIGN_UP} component={YSaatioSignUpPage} />,
    <Route key={YSaatioRoutes.SIGN_IN} path={YSaatioRoutes.SIGN_IN} component={YSaatioSignInPage} />,
    <Route
      key={YSaatioRoutes.RESET_PASSWORD}
      path={YSaatioRoutes.RESET_PASSWORD}
      component={YSaatioResetPasswordPage}
    />,
    <AuthenticatedRoute
      key={YSaatioRoutes.HOME}
      path={YSaatioRoutes.HOME}
      component={YSaatioTerminationHomePage}
      authenticationRedirectRoute={YSaatioRoutes.SIGN_UP}
      exact
    />,
    <AuthenticatedRoute
      key={YSaatioRoutes.FORM}
      path={YSaatioRoutes.FORM}
      component={YSaatioTerminationFormPage}
      authenticationRedirectRoute={YSaatioRoutes.SIGN_UP}
    />,
  ];

  return (
    <Router history={customHistory}>
      <Switch>
        <Route path={Routes.SIGN_IN} component={SignInPage} />
        <Route path={Routes.RESET_PASSWORD} component={ResetPasswordPage} />
        <Route path={Routes.SIGN_UP} component={SignUpPage} />
        <Route path={Routes.Administrative.FORM_REVIEW} component={FormReviewPage} />
        <Route path={Routes.Administrative.IMPERSONATE} component={ImpersonationLoginPage} />

        {ySaatioContractTerminationRoutes.map((route) => route)}

        <AuthenticatedRoute exact path={Routes.HOME} component={HomePage} />
        <AuthenticatedRoute path={Routes.PROFILE} component={ProfilePage} />
        <AuthenticatedRoute exact path={Routes.OwnForms.ALL_FORMS} component={OwnFormsPage} />
        <AuthenticatedRoute exact path={Routes.OwnForms.FORM} component={FormPage} />
        <AuthenticatedRoute path={Routes.OwnForms.UPDATE_FORM} component={UpdateFormPage} />
        {/* <AuthenticatedRoute path={Routes.SEARCH} component={SearchPage} /> */}
        <AuthenticatedRoute path={Routes.Tenancy.PAYMENT_SUCCESS} component={BillingPage} />
        <AuthenticatedRoute path={Routes.Tenancy.PAYMENT_ERROR} component={BillingPage} />
        <AuthenticatedRoute exact path={Routes.CONTACT_REQUEST_SUCCESS} component={ContactRequestSuccessPage} />
        {/* <AuthenticatedRoute path={Routes.APARTMENT_SWAP} component={ApartmentSwapPage} /> */}
        {/* <AuthenticatedAdminRoute path={Routes.Administrative.HOME} component={AdministrativeHome} /> */}
        <AuthenticatedRoute path={Routes.ResidentCommittee.MEETING_MINUTES} component={MeetingMinutesPage} />
        <AuthenticatedRoute path={Routes.ResidentCommittee.ACTION_PLAN} component={ActionPlanPage} />
        <AuthenticatedRoute path={Routes.ResidentCommittee.ACTION_REPORT} component={ActionReportPage} />
        <AuthenticatedRoute
          path={Routes.ResidentCommittee.GENERAL_EXPENSE_REPORT}
          component={GeneralExpenseReportPage}
        />
        <AuthenticatedRoute path={Routes.ResidentCommittee.TRAVEL_EXPENSE_REPORT} component={TravelExpenseReportPage} />
        <AuthenticatedRoute path={Routes.ResidentCommittee.EVENT_INVITE} component={EventInvitePage} />
        {menuRoutes.map((route) => route)}
        {menuItems && <Redirect to={Routes.HOME} />}
      </Switch>
    </Router>
  );
};

export default RoutingRules;
